import { useEffect, useMemo, useState } from "react"
import apiClient from "../../../Utils/apiClient"
import {
  Button,
  Input,
  Modal,
  Timeline,
  message,
} from "antd"
import PanelLayout from "../../../Layout/PanelLayout"
import TableBox from "../../../components/TableBox/TableBox"
import StatusTag from "../../../components/StatusTag/StatusTag"
import DeviceCell from "../../../components/DeviceCell/DeviceCell"
import { useHeaderContext } from "../../../Utils/HeaderContext"
import { renderDate } from "../../../Utils/utils"
import { getStoredPageSize } from "../../../Utils/TableUtils/TableUtils"
import { appStatusLabel, normalizeAppStatus } from "../../../Utils/appStatus"
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons"
import "../AppManagement.scss"
import "../../Operations/Operations.scss"

const KPI_TONES = {
  all: { tone: "#2563eb", soft: "rgba(59, 130, 246, 0.14)" },
  pending: { tone: "#d97706", soft: "rgba(245, 158, 11, 0.16)" },
  installing: { tone: "#0891b2", soft: "rgba(56, 189, 248, 0.14)" },
  installed: { tone: "#059669", soft: "rgba(34, 197, 94, 0.14)" },
  failed: { tone: "#dc2626", soft: "rgba(248, 113, 113, 0.14)" },
  rejected: { tone: "#64748b", soft: "rgba(148, 163, 184, 0.12)" },
  uninstalled: { tone: "#7c3aed", soft: "rgba(167, 139, 250, 0.14)" },
}

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Requested" },
  { key: "installing", label: "Installing" },
  { key: "installed", label: "Installed" },
  { key: "failed", label: "Failed" },
  { key: "rejected", label: "Rejected" },
  { key: "uninstalled", label: "Uninstalled" },
]

const AppRequests = () => {
  const { setTaskId, setRender, render } = useHeaderContext()
  const page = "App Requests"
  const [refresh, setRefresh] = useState(false)
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [status, setStatus] = useState("pending")
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: getStoredPageSize(page) || 10,
  })
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState("")
  const [selectedRejectItem, setSelectedRejectItem] = useState(null)
  const [isUninstallModalOpen, setIsUninstallModalOpen] = useState(false)
  const [uninstallReason, setUninstallReason] = useState("")
  const [selectedUninstallItem, setSelectedUninstallItem] = useState(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false)

  useEffect(() => {
    getData()
  }, [refresh, render])

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim().toLowerCase())
      setPagination((prev) => ({ ...prev, page: 1 }))
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  const getData = async () => {
    setLoading(true)
    try {
      const res = await apiClient("request/get-all")
      const allRequests = res?.data || []
      setRows(
        allRequests.map((item) => ({
          ...item,
          statusKey: normalizeAppStatus(item.status),
        }))
      )
    } catch (err) {
      console.error("Failed to load app requests", err)
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  const submitRejectApp = async () => {
    if (!rejectReason.trim()) {
      message.warning("Please provide a valid reason for this rejection.")
      return
    }
    try {
      const result = await apiClient("request/reject", "POST", {
        requestId: selectedRejectItem?._id,
        appData: [{ requestId: selectedRejectItem?._id }],
        userEmail: selectedRejectItem?.requestedEmail,
        user: selectedRejectItem?.requestedFrom,
        reason: rejectReason.trim(),
      })
      if (!result?.success) {
        message.error(result?.message || "Failed to reject")
        return
      }
      setRefresh((pre) => !pre)
      message.success(result.message)
    } catch (err) {
      console.log("error while rejecting the application", err)
      message.error("Failed to reject")
    } finally {
      setIsRejectModalOpen(false)
      setRejectReason("")
      setSelectedRejectItem(null)
    }
  }

  const handleApprove = async (item) => {
    await handleApproveMany([item])
  }

  const handleApproveMany = async (items) => {
    const targets = items.filter(
      (item) => item.statusKey === "pending" || item.statusKey === "failed"
    )
    if (!targets.length) {
      message.info("Select requested or failed items to approve.")
      return
    }
    try {
      const response = await apiClient(`request/approve`, "POST", {
        requestId: targets[0]._id,
        appData: targets.map((item) => ({ requestId: item._id })),
        hostname: targets[0]?.requestedFrom,
        userEmail: targets[0]?.requestedEmail,
      })
      if (!response?.success) {
        message.error(response?.message || "Failed to approve request")
        return
      }
      message.success(response.message)
      if (response.taskId) setTaskId(response.taskId)
      setRender((pre) => !pre)
      setRefresh((pre) => !pre)
      setSelectedRowKeys([])
    } catch (err) {
      message.error("Failed to approve request")
    }
  }

  const submitRejectMany = async (items, reason) => {
    const targets = items.filter(
      (item) => item.statusKey === "pending" || item.statusKey === "failed"
    )
    if (!targets.length) {
      message.info("Select requested or failed items to reject.")
      return
    }
    try {
      const result = await apiClient("request/reject", "POST", {
        requestId: targets[0]._id,
        appData: targets.map((item) => ({ requestId: item._id })),
        userEmail: targets[0]?.requestedEmail,
        user: targets[0]?.requestedFrom,
        reason,
      })
      if (!result?.success) {
        message.error(result?.message || "Failed to reject")
        return
      }
      setRefresh((pre) => !pre)
      setSelectedRowKeys([])
      message.success(result.message)
    } catch (err) {
      message.error("Failed to reject")
    }
  }

  const submitUninstallApp = async () => {
    if (!uninstallReason.trim()) {
      message.warning("Please provide a valid reason for uninstall.")
      return
    }
    try {
      const response = await apiClient(`request/uninstall`, "POST", {
        requestId: selectedUninstallItem?._id,
        appData: [{ requestId: selectedUninstallItem?._id }],
        hostname: selectedUninstallItem?.requestedFrom,
        userEmail: selectedUninstallItem?.requestedEmail,
        reason: uninstallReason.trim(),
      })
      if (!response?.success) {
        message.error(response?.message || "Failed to start uninstall")
        return
      }
      message.success(response.message)
      if (response.taskId) setTaskId(response.taskId)
      setRender((pre) => !pre)
      setRefresh((pre) => !pre)
    } catch (err) {
      console.log("err on Handle Uninstall", err)
      message.error("Failed to start uninstall")
    } finally {
      setIsUninstallModalOpen(false)
      setUninstallReason("")
      setSelectedUninstallItem(null)
    }
  }

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      if (status !== "all" && row.statusKey !== status) return false
      if (!search) return true
      const blob = [
        row.requestedFrom,
        row.requestedEmail,
        row.appData?.appName,
        row.appData?.packageId,
        row.reason,
        row.appData?.version,
      ]
        .join(" ")
        .toLowerCase()
      return blob.includes(search)
    })
  }, [rows, status, search])

  const summary = useMemo(() => {
    const base = search
      ? rows.filter((row) =>
          [row.requestedFrom, row.requestedEmail, row.appData?.appName, row.reason]
            .join(" ")
            .toLowerCase()
            .includes(search)
        )
      : rows
    const counts = {
      all: base.length,
      pending: 0,
      installing: 0,
      installed: 0,
      failed: 0,
      rejected: 0,
      uninstalled: 0,
    }
    base.forEach((row) => {
      if (counts[row.statusKey] !== undefined) counts[row.statusKey] += 1
    })
    return counts
  }, [rows, search])

  const pageRows = filtered.slice(
    (pagination.page - 1) * pagination.pageSize,
    pagination.page * pagination.pageSize
  )

  const columns = [
    {
      title: "App",
      key: "app",
      render: (_, record) => (
        <div className="app-mgmt__app">
          {record.appData?.iconUrl ? (
            <img src={record.appData.iconUrl} alt="" className="app-mgmt__icon" />
          ) : null}
          <div>
            <div className="app-mgmt__name">{record.appData?.appName || "—"}</div>
            <div className="app-mgmt__muted">
              {(record.appData?.source || "choco") === "winget" ? "Winget" : "Chocolatey"}
              {record.appData?.packageId ? ` · ${record.appData.packageId}` : ""}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Device",
      dataIndex: "requestedFrom",
      key: "requestedFrom",
      width: 280,
      render: (hostname, record) => (
        <DeviceCell
          record={{
            _id: record.deviceId,
            deviceStatus: record.deviceStatus,
            sysInfo: record.sysInfo,
          }}
          hostname={hostname}
          user={record.requestedEmail}
        />
      ),
    },
    {
      title: "Justification",
      dataIndex: "reason",
      key: "reason",
      ellipsis: true,
      render: (value) => value || "—",
    },
    {
      title: "Version",
      key: "version",
      width: 110,
      render: (_, record) => record.appData?.version || "—",
    },
    {
      title: "Status",
      key: "status",
      width: 130,
      render: (_, record) => <StatusTag status={appStatusLabel(record.statusKey)} />,
    },
    {
      title: "Timeline",
      key: "events",
      width: 220,
      render: (_, record) =>
        Array.isArray(record.events) && record.events.length ? (
          <Timeline
            items={record.events.slice(-4).map((event) => ({
              children: `${event.status || ""}${event.detail ? ` · ${event.detail}` : ""}`,
            }))}
          />
        ) : (
          "—"
        ),
    },
    {
      title: "Error",
      key: "lastError",
      ellipsis: true,
      render: (_, record) => record.lastError || record.rejectReason || "—",
    },
    {
      title: "When",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      render: (value) => renderDate(value),
    },
    {
      title: "",
      key: "actions",
      width: 220,
      align: "right",
      render: (_, item) => {
        const key = item.statusKey
        if (key === "installed") {
          return (
            <div className="app-mgmt__actions">
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  setSelectedUninstallItem(item)
                  setIsUninstallModalOpen(true)
                }}
              >
                Uninstall
              </Button>
            </div>
          )
        }
        if (key === "pending" || key === "failed") {
          return (
            <div className="app-mgmt__actions">
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => handleApprove(item)}
              >
                {key === "failed" ? "Retry" : "Approve"}
              </Button>
              <Button
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => {
                  setSelectedRejectItem(item)
                  setIsRejectModalOpen(true)
                }}
              >
                Reject
              </Button>
            </div>
          )
        }
        return <span className="app-mgmt__muted">—</span>
      },
    },
  ]

  return (
    <PanelLayout
      title="Software requests"
      subtitle="Approve, reject, retry or uninstall applications that users asked for, and follow each request through install."
      actions={
        <>
          <Input
            className="ops-search"
            prefix={<SearchOutlined />}
            allowClear
            placeholder="Search app, device, email, or justification"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Button onClick={() => setRefresh((pre) => !pre)} loading={loading}>
            Refresh
          </Button>
        </>
      }
    >
      <div className="ops-kpis">
        {STATUS_FILTERS.map((item) => {
          const tones = KPI_TONES[item.key] || KPI_TONES.all
          return (
            <button
              type="button"
              key={item.key}
              className={`ops-kpi${status === item.key ? " is-active" : ""}`}
              style={{ "--act-tone": tones.tone, "--act-soft": tones.soft }}
              onClick={() => {
                setStatus(item.key)
                setPagination((prev) => ({ ...prev, page: 1 }))
              }}
            >
              <div>
                <span className="ops-kpi-label">{item.label}</span>
                <strong>{summary[item.key] ?? 0}</strong>
              </div>
            </button>
          )
        })}
      </div>

      <div className="ops-toolbar">
        <span className="ops-toolbar__count">
          {filtered.length} request{filtered.length === 1 ? "" : "s"}
          {status !== "all"
            ? ` · ${STATUS_FILTERS.find((item) => item.key === status)?.label}`
            : ""}
        </span>
        <div className="dashboard-filter-chips">
          <span>Status</span>
          {STATUS_FILTERS.map((item) => (
            <Button
              key={item.key}
              size="small"
              type={status === item.key ? "primary" : "default"}
              onClick={() => {
                setStatus(item.key)
                setPagination((prev) => ({ ...prev, page: 1 }))
              }}
            >
              {item.label}
            </Button>
          ))}
        </div>
      </div>

      {selectedRowKeys.length > 0 ? (
        <div className="app-mgmt__bulk">
          <span>{selectedRowKeys.length} selected</span>
          <Button
            type="primary"
            onClick={() =>
              handleApproveMany(rows.filter((row) => selectedRowKeys.includes(row._id)))
            }
          >
            Approve selected
          </Button>
          <Button
            danger
            onClick={() => {
              setBulkRejectOpen(true)
            }}
          >
            Reject selected
          </Button>
          <Button onClick={() => setSelectedRowKeys([])}>Clear</Button>
        </div>
      ) : null}

      <TableBox
        page={page}
        title={null}
        loading={loading}
        columns={columns}
        className="dashboard-inventory-table"
        data={pageRows}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: filtered.length,
          showSizeChanger: true,
        }}
        onChange={(pager) =>
          setPagination({ page: pager.current, pageSize: pager.pageSize })
        }
      />

      <Modal
        title="Reject application"
        open={isRejectModalOpen}
        onOk={submitRejectApp}
        onCancel={() => {
          setIsRejectModalOpen(false)
          setRejectReason("")
          setSelectedRejectItem(null)
        }}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        <p>Why are you rejecting {selectedRejectItem?.appData?.appName}?</p>
        <Input.TextArea
          rows={4}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Reason for rejection"
        />
      </Modal>
      <Modal
        title="Uninstall application"
        open={isUninstallModalOpen}
        onOk={submitUninstallApp}
        onCancel={() => {
          setIsUninstallModalOpen(false)
          setUninstallReason("")
          setSelectedUninstallItem(null)
        }}
        okText="Uninstall"
        okButtonProps={{ danger: true }}
      >
        <p>Why uninstall {selectedUninstallItem?.appData?.appName}?</p>
        <Input.TextArea
          rows={4}
          value={uninstallReason}
          onChange={(e) => setUninstallReason(e.target.value)}
          placeholder="Reason for uninstall"
        />
      </Modal>
      <Modal
        title="Reject selected requests"
        open={bulkRejectOpen}
        onOk={async () => {
          if (!rejectReason.trim()) {
            message.warning("Please provide a valid reason for this rejection.")
            return
          }
          await submitRejectMany(
            rows.filter((row) => selectedRowKeys.includes(row._id)),
            rejectReason.trim()
          )
          setBulkRejectOpen(false)
          setRejectReason("")
        }}
        onCancel={() => {
          setBulkRejectOpen(false)
          setRejectReason("")
        }}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        <p>Why are you rejecting {selectedRowKeys.length} request(s)?</p>
        <Input.TextArea
          rows={4}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Reason for rejection"
        />
      </Modal>
    </PanelLayout>
  )
}

export default AppRequests
